import { defineSettingsScreen } from '@heidi/runtime/shell';

/**
 * The `/settings/evidence` screen body.
 *
 * `id` is the settings route slug (`ROUTE_TO_SLUG['/settings/evidence']`),
 * which is what lets a package serve an existing settings screen with the
 * sidebar and the IA untouched: the loader resolves the route to this
 * contribution and falls back to the app's own importer for every route no
 * package claims.
 *
 * `section: 'personal'` is measured, not chosen — the screen renders
 * `group=` the "Personal" message in `SettingsPageHeader`, and the sidebar
 * lists Evidence under that group (`SETTINGS_SECTION_SIDEBAR_LABELS.personal`
 * records the label source).
 *
 * `order: 0` inside the section's `[0, 999]` band. It is inert today — screens
 * are served by id and the sidebar is hand-written — so a low value claims no
 * position anyone else has to renumber around.
 *
 * `builds: 'all'`: the screen ships on every build profile, and
 * `useSettingsList` renders the Evidence item under the same
 * `hasEvidenceAccess && !isEvidenceAccessPending` entitlement condition on
 * every profile. That says the screen is REACHABLE on-prem for an entitled
 * user, not that everything on it works there: sections backed by the
 * `/evidence` aggregate (integrations, SharePoint, collections) have no route on
 * an appliance, and closing those reads is the job of the consts in
 * `src/utils/backendSurfaces.ts` and of the on-prem flag bundle, not of this
 * field, which nothing reads at runtime.
 */
export const evidenceSettingsScreen = defineSettingsScreen({
  id: 'evidence',
  section: 'personal',
  order: 0,
  // The thunk names the export rather than relying on a default one: the screen
  // is a named export like every other component in this package, and
  // `ContributionLoadThunk` wants `{ default }`. The mapping is where the two
  // conventions meet, and it keeps the module free of a default export nothing
  // else would use. It points at the SCREEN MODULE, never at the `.` door — the
  // door also carries the answer blocks, the timeline blocks, the leaderboard
  // and the in-session surface, and a contribution is the one consumer that can
  // name the module directly.
  load: () =>
    import('../ui/settings/EvidenceSettings').then((module) => ({
      default: module.EvidenceSettings,
    })),
  builds: 'all',
});

/**
 * Declares the team Evidence settings screen through the shell.
 */
export const teamEvidenceSettingsScreen = defineSettingsScreen({
  id: 'team-evidence',
  section: 'admin',
  order: 1,
  load: () =>
    import('../ui/settings/team/TeamEvidenceSettings').then((module) => ({
      default: module.TeamEvidenceSettingsScreen,
    })),
  builds: 'all',
});
